import { useEffect } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LEGAL_NAV_ITEMS,
  LEGAL_PAGES,
  formatLegalLastUpdated,
  getLegalPage,
  getLegalPageBySlug,
  isLegalSectionId,
  isLegalSlug,
  legacyLegalRedirectPath,
  type LegalBlock,
  type LegalPage,
  type LegalSectionId,
} from "@shared/legal-pages";
import {
  Shield,
  FileText,
  Cookie,
  Mail,
  Users,
} from "lucide-react";

const SECTION_ICONS: Record<LegalSectionId, typeof Users> = {
  about: Users,
  contact: Mail,
  legal: FileText,
  privacy: Shield,
  'social-privacy': Shield,
  cookies: Cookie,
  terms: FileText,
};

/** Render text with simple markdown links [label](url) as Link or <a>. */
function renderTextWithLinks(text: string) {
  const parts: ReactNode[] = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const label = match[1];
    const href = match[2];
    if (href.startsWith('/')) {
      parts.push(
        <Link key={key++} href={href} className="text-primary underline hover:opacity-80">
          {label}
        </Link>
      );
    } else {
      parts.push(
        <a
          key={key++}
          href={href}
          className="text-primary underline hover:opacity-80"
          rel="noopener noreferrer"
        >
          {label}
        </a>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : text;
}

function renderBlock(block: LegalBlock, index: number) {
  return (
    <section key={index} className="space-y-3">
      {block.heading ? (
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
          {block.heading}
        </h3>
      ) : null}
      {block.paragraphs?.map((p, i) => (
        <p key={i} className="text-slate-600 dark:text-slate-300">
          {renderTextWithLinks(p)}
        </p>
      ))}
      {block.definitionLines ? (
        <div className="space-y-2 text-slate-600 dark:text-slate-300">
          {block.definitionLines.map((line, i) => (
            <p key={i}>
              <strong>{line.label}:</strong> {line.value}
            </p>
          ))}
        </div>
      ) : null}
      {block.list ? (
        <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 ml-4">
          {block.list.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      ) : null}
      {block.subBlocks?.map((sub, i) => (
        <div key={i} className="space-y-1">
          <h4 className="font-medium text-slate-900 dark:text-white">{sub.heading}</h4>
          <p className="text-slate-600 dark:text-slate-300">{sub.paragraph}</p>
        </div>
      ))}
    </section>
  );
}

function LegalPageContent({ page }: { page: LegalPage }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
          {page.title}
        </h1>
        {page.showLastUpdated ? (
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            Última actualización: {formatLegalLastUpdated()}
          </p>
        ) : null}
        {page.intro ? (
          <p className="text-slate-600 dark:text-slate-300 mb-8">{page.intro}</p>
        ) : null}
      </div>
      <div className="space-y-8">
        {page.blocks.map((block, i) => renderBlock(block, i))}
      </div>
    </div>
  );
}

function useResolvedLegalPage(): LegalPage {
  const params = useParams<{ section?: string; slug?: string }>();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const fromQuery = qs.get('section');
    if (fromQuery) {
      setLocation(legacyLegalRedirectPath(fromQuery));
      return;
    }
    if (location === '/legal' || location.startsWith('/legal?')) {
      setLocation('/sobre-statemia');
      return;
    }
    const legacyMatch = location.match(/^\/legal\/([^/?#]+)/);
    if (legacyMatch) {
      setLocation(legacyLegalRedirectPath(legacyMatch[1]));
    }
  }, [location, setLocation]);

  const slugParam = params.slug;
  if (slugParam && isLegalSlug(slugParam)) {
    return getLegalPageBySlug(slugParam) || LEGAL_PAGES.about;
  }

  const sectionParam = params.section;
  if (sectionParam && isLegalSectionId(sectionParam)) {
    const page = getLegalPage(sectionParam);
    if (page?.slug) return page;
    return LEGAL_PAGES.privacy;
  }

  // Pathname fallback (e.g. direct match without param name)
  const pathSlug = location.replace(/^\//, '').split('?')[0];
  if (isLegalSlug(pathSlug)) {
    return getLegalPageBySlug(pathSlug) || LEGAL_PAGES.about;
  }

  return LEGAL_PAGES.about;
}

export default function Legal() {
  const page = useResolvedLegalPage();

  useEffect(() => {
    document.title = `${page.title} | Statemia`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', page.metaDescription);
  }, [page]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Legal y soporte</CardTitle>
              </CardHeader>
              <CardContent>
                <nav className="space-y-2" aria-label="Secciones legales">
                  {LEGAL_NAV_ITEMS.map((item) => {
                    const Icon = SECTION_ICONS[item.id];
                    const active = item.id === page.id;
                    return (
                      <Link
                        key={item.id}
                        href={item.href}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-colors ${
                          active
                            ? 'bg-primary text-white'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {item.title}
                      </Link>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-3">
            <Card>
              <CardContent className="p-8">
                <LegalPageContent page={page} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
